/**
 * Board-space geometry — F-121 board-first canvas (docs/04 §5).
 *
 * The board is one infinite coordinate system ("board space"): element
 * `x`/`y`/`w`/`h` keys, board anchors, and the grid's position are all stored
 * in board units. The client renders through a single camera transform so
 * grid canvas, element overlay, and comment pins can never misalign — these
 * helpers ARE that shared transform, kept here (pure, dependency-free) so
 * client layers and server-side geometry (e.g. AI placement) agree exactly.
 *
 * Conventions:
 *  - Screen space = CSS pixels relative to the viewport's top-left.
 *  - `Camera.x`/`Camera.y` = the board-space point currently under the screen
 *    origin (viewport top-left), in board units.
 *  - `Camera.zoom` = screen pixels per board unit (F-121: 25%–500%).
 *
 * NOTE: the camera is per-user VIEW state (local/awareness), never CRDT doc
 * state — two users panning are not editing the board.
 */
/**
 * The grid's FIXED position in board space (F-21: exactly one grid per
 * board, F-121: it sits on the canvas at a constant spot). Cell rects in
 * board space = GRID_ORIGIN + accumulated row/column extents.
 */
export const GRID_ORIGIN = Object.freeze({ x: 0, y: 0 });
/**
 * F-121 zoom bounds: 25%–500%.
 *
 * The ceiling rose from 300% (2026-08-30) because reading a dense translated
 * cell at 300% still meant leaning at the screen. Zoom is a CSS transform on
 * live DOM (docs/04, BoardCanvas), so higher zoom renders FEWER nodes, not
 * bigger bitmaps — text stays vector-crisp and the row window (core/
 * row-window.ts) narrows with it. The cost is the counter-scaled overlay
 * chrome (hairlines, handles), which already divides by zoom.
 */
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 5;
/** Clamp a requested zoom factor to the F-121 range. */
export function clampZoom(zoom) {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}
/** Board units → screen CSS px under `camera`. Pure; inverse of `screenToBoard`. */
export function boardToScreen(camera, pt) {
    return {
        x: (pt.x - camera.x) * camera.zoom,
        y: (pt.y - camera.y) * camera.zoom,
    };
}
/**
 * Screen CSS px → board units under `camera`. Pure; inverse of
 * `boardToScreen`. This is the F-30 drop-point mapping: a pointer position
 * converts to board coordinates, then hit-tests grid cell vs empty canvas.
 */
export function screenToBoard(camera, pt) {
    return {
        x: pt.x / camera.zoom + camera.x,
        y: pt.y / camera.zoom + camera.y,
    };
}
//# sourceMappingURL=board-space.js.map